// Эндпоинты табеля: список периодов, сетка, заполнение, закрытие.
//
// Формы — строго по contracts/openapi/bark-core.v1.yaml. Контракт правит только сессия ядра,
// здесь его зеркало, а не вольный пересказ: поле, придуманное на фронте, даёт зелёную сборку и
// пустой экран.
//
// Часы ходят СТРОКОЙ, не числом. Число с плавающей точкой в браузере теряет сотые, а из часов
// считается начисление: «сумма за период × факт ÷ норма».

import { request } from '@/shared/api/client';
import { queryString } from '@/shared/api/query';
import type { DecimalString, ListResponse } from '@/shared/api/types';

/** Отметка ячейки. «Отработано» — единственная, при которой бывают часы. */
export type ShiftMark = 'worked' | 'absent' | 'sick' | 'day_off';

/** Способ ведения месяца. `month_total` у заказчика основной: так ведутся 59 объектов из 60. */
export type EntryMode = 'month_total' | 'by_shift';

export type TimesheetStatus = 'draft' | 'review' | 'closed';

export interface TotalsOut {
  /** Должно быть. `null` — норматива нет: он не заполнен у 24 объектов заказчика из 60. */
  norm: DecimalString | null;
  norm_computed: DecimalString | null;
  norm_override: DecimalString | null;
  /** Вышло: сумма фактических часов по объекту. */
  fact: DecimalString;
  /** Недобор в часах. Отрицательное значение — переработка, а не ошибка. */
  shortfall: DecimalString | null;
  /** Укомплектованность часами. `null`, когда норматива нет или он нулевой. */
  percent: DecimalString | null;
  days: number;
  /**
   * Сколько часов объект обязан закрывать В СУТКИ: норматив месяца, делённый на дни.
   *
   * По нему сетка находит дни, где пост не закрыт. Делением, а не суммой часов по постам:
   * так сумма дневных недоборов равна месячному ровно, а ряд чисел, который не сходится,
   * человек читает как «программа посчитала неверно».
   */
  norm_per_day: DecimalString | null;
  /** Отмечена ли хоть одна смена: отличает пустой табель от нулевого результата. */
  has_any_shift: boolean;
  unknown_reason: string | null;
}

export interface PeriodOut {
  /** `null`, если табель за этот месяц ещё не заводили. */
  id: string | null;
  site_id: string;
  site_name: string;
  year: number;
  month: number;
  status: TimesheetStatus;
  entry_mode: EntryMode;
  totals: TotalsOut;
  closed_at: string | null;
  closed_by_name: string | null;
  reopened_at: string | null;
  reopen_basis: string | null;
  /** Примечания табельщика по объекту за этот месяц. Пусто — не писали. */
  note: string | null;
  /** Кто написал последним. Табельщиков у объекта бывает двое, и это первый вопрос к тексту. */
  note_author: string | null;
  note_at: string | null;
  version: number;
}

export interface CellOut {
  work_date: string;
  planned_hours: DecimalString | null;
  /** Факт. `null` — «ещё не отмечали», «0» — «отмечено, что часов нет». Это разные вещи. */
  hours: DecimalString | null;
  mark: ShiftMark;
  /** День, ночь, сутки. `null` — тип не проставлен: из часов он не выводится. */
  shift_type: ShiftType | null;
  is_month_total: boolean;
}

export interface GridRowOut {
  /** Строка сетки — НАЗНАЧЕНИЕ: переведённый внутри месяца человек идёт двумя строками. */
  assignment_id: string;
  employee_id: string;
  employee_name: string;
  post_id: string;
  post_name: string;
  schedule_type: string | null;
  date_from: string;
  date_to: string | null;
  cells: CellOut[];
  total_hours: DecimalString;
  month_total_hours: DecimalString | null;
  /** Тип смен строки, если он у всех один. `null` — не проставлен либо разный. */
  shift_type: ShiftType | null;
  /** У смен строки типы разные. Отдельно от пустого: «не проставлен» и «разный» — не одно. */
  shift_type_mixed: boolean;
}

export interface GridOut {
  period: PeriodOut;
  days: string[];
  rows: GridRowOut[];
  can_edit: boolean;
  blocked_reason: string | null;
}

export interface GenerateOut {
  created: number;
  skipped: number;
  rows_without_schedule: string[];
  totals: TotalsOut;
}

export interface FillOut {
  filled: number;
  totals: TotalsOut;
}

/** Тип смены из тз 5.3. Пусто — законное значение: «табельщик не проставил». */
export type ShiftType = 'day' | 'night' | 'full_day';

/** Как тип смены называется человеку. Слово целиком: сокращения в табеле не читаются. */
export const НАЗВАНИЕ_ТИПА_СМЕНЫ: Readonly<Record<ShiftType, string>> = {
  day: 'День',
  night: 'Ночь',
  full_day: 'Сутки',
};

/** Примечания табельщика по объекту за месяц. Пустой текст снимает примечание. */
export interface TimesheetNoteIn {
  note: string | null;
  /** Версия табеля: табельщиков у объекта бывает двое, и писать они будут по очереди. */
  version: number;
}

export interface ShiftTypeIn {
  assignment_id: string;
  /** `null` снимает тип. */
  shift_type: ShiftType | null;
}

export interface CopyDayOut {
  /** Какой день повторили; `null` — отмечать было нечего. */
  source_date: string | null;
  filled: number;
  skipped: number;
  rows: number;
  totals: TotalsOut;
}

export interface CellIn {
  assignment_id: string;
  work_date: string;
  hours: string | null;
  mark: ShiftMark;
}

export interface MonthTotalIn {
  assignment_id: string;
  hours: string | null;
}

export interface Период {
  readonly year: number;
  readonly month: number;
}

/** Год и месяц в строку запроса. Отдельная функция: иначе тип периода обрастает индексом. */
function периодом(период: Период, ещё: Record<string, string | undefined> = {}): string {
  return queryString({ year: период.year, month: период.month, ...ещё });
}

export function fetchPeriods(
  period: Период,
  params: { q?: string } = {},
): Promise<ListResponse<PeriodOut>> {
  return request(`/api/v1/timesheets${периодом(period, params)}`);
}

/**
 * Месяц человека на одном объекте: сколько вышло, сколько смен, сколько невыходов.
 *
 * Строка на ПАРУ «месяц и объект»: человека переводят внутри месяца, и слить два объекта в
 * одну строку значит потерять, где именно он стоял.
 */
export interface EmployeeMonthOut {
  readonly year: number;
  readonly month: number;
  readonly period_title: string;
  readonly site_id: string;
  readonly site_name: string;
  readonly status: string;
  readonly hours: string;
  readonly shifts: number;
  readonly absences: number;
  readonly shift_type: ShiftType | null;
}

export function fetchEmployeeMonths(employeeId: string): Promise<ListResponse<EmployeeMonthOut>> {
  return request(`/api/v1/timesheets/employees/${employeeId}/months`);
}

export function fetchGrid(siteId: string, period: Период): Promise<GridOut> {
  return request(`/api/v1/timesheets/${siteId}/grid${периодом(period)}`);
}

export function generateSchedule(siteId: string, period: Период): Promise<GenerateOut> {
  return request(`/api/v1/timesheets/${siteId}/generate${периодом(period)}`, {
    method: 'POST',
  });
}

export function fillByPlan(siteId: string, period: Период): Promise<FillOut> {
  return request(`/api/v1/timesheets/${siteId}/fill-by-plan${периодом(period)}`, {
    method: 'POST',
  });
}

export function setCell(siteId: string, period: Период, body: CellIn): Promise<GridOut> {
  return request(`/api/v1/timesheets/${siteId}/cell${периодом(period)}`, {
    method: 'PUT',
    body,
  });
}

export function setCells(siteId: string, period: Период, cells: CellIn[]): Promise<GridOut> {
  return request(`/api/v1/timesheets/${siteId}/cells${периодом(period)}`, {
    method: 'PUT',
    body: { cells },
  });
}

export function setMonthTotals(
  siteId: string,
  period: Период,
  body: MonthTotalIn[],
): Promise<GridOut> {
  return request(`/api/v1/timesheets/${siteId}/month-totals${периодом(period)}`, {
    method: 'PUT',
    body,
  });
}

/**
 * Тип смены на всю строку месяца.
 *
 * На строку, а не на ячейку: человек ходит на посту одним типом весь месяц, а у ячейки уже
 * есть своё единственное поведение — часы.
 */
export function setShiftType(siteId: string, period: Период, body: ShiftTypeIn): Promise<GridOut> {
  return request(`/api/v1/timesheets/${siteId}/shift-type${периодом(period)}`, {
    method: 'PUT',
    body,
  });
}

/** Повторить день до конца месяца. Уже отмеченное не перезаписывается. */
export function copyDay(siteId: string, period: Период, sourceDate?: string): Promise<CopyDayOut> {
  return request(`/api/v1/timesheets/${siteId}/copy-day${периодом(period)}`, {
    method: 'POST',
    body: { source_date: sourceDate ?? null },
  });
}

/** Черновик либо на проверке. Закрытие сюда не входит: у него своё право. */
export function setTimesheetStatus(
  siteId: string,
  period: Период,
  status: 'draft' | 'review',
  version: number,
): Promise<GridOut> {
  return request(`/api/v1/timesheets/${siteId}/status${периодом(period)}`, {
    method: 'PUT',
    body: { status, version },
  });
}

export function setEntryMode(siteId: string, period: Период, mode: EntryMode): Promise<GridOut> {
  return request(`/api/v1/timesheets/${siteId}/entry-mode${периодом(period, { mode })}`, {
    method: 'PUT',
  });
}

/**
 * Примечания табельщика по объекту за месяц.
 *
 * Версия уезжает в теле, а не в адресе: она защищает от гонки двух табельщиков, и потерять её
 * при копировании ссылки нельзя.
 */
export function setTimesheetNote(
  siteId: string,
  period: Период,
  body: TimesheetNoteIn,
): Promise<GridOut> {
  return request(`/api/v1/timesheets/${siteId}/note${периодом(period)}`, {
    method: 'PUT',
    body,
  });
}

export function closePeriod(siteId: string, period: Период, version: number): Promise<GridOut> {
  return request(`/api/v1/timesheets/${siteId}/close${периодом(period)}`, {
    method: 'POST',
    body: { version },
  });
}

export function reopenPeriod(
  siteId: string,
  period: Период,
  version: number,
  basis: string,
): Promise<GridOut> {
  return request(`/api/v1/timesheets/${siteId}/reopen${периодом(period)}`, {
    method: 'POST',
    body: { version, basis },
  });
}

// ── «Внести часы»: итог за месяц по одному человеку, из расчёта сотрудника ─────────────────────
//
// Не то же самое, что `setMonthTotals`: тот переключает способ ведения ВСЕГО табеля и гасит часы
// остальных людей объекта. Этот пишет одну строку итога одного назначения и больше ничего не
// трогает, а если у человека в месяце уже есть посменные часы, отказывает.

/** Что с ведомостью объекта за месяц после внесения часов. */
export interface PersonHoursPayroll {
  payroll_id: string;
  status: string;
  /** Состояние словами: «Черновик», «Утверждена». */
  status_title: string;
  /** Нет — человека в этой ведомости нет, и часы сами в деньги не попадут. */
  person_in_payroll: boolean;
  /** Часы в строке ведомости не равны часам табеля: ведомость надо пересчитать. */
  recalc_needed: boolean;
}

export interface PersonMonthHoursOut {
  employee_id: string;
  site_id: string;
  site_name: string;
  year: number;
  month: number;
  assignment_id: string;
  /** Итог за месяц, как записан в табеле: «60.00». */
  hours: DecimalString;
  /** Норма по назначению: «372.0». Пусто — у назначения нормы нет. */
  norm_hours: DecimalString | null;
  /** Больше нормы: часы записаны, но число стоит проверить. */
  over_norm: boolean;
  warnings: string[];
  /** Способ ведения табеля внесение не меняет. */
  entry_mode: EntryMode;
  /** Пусто — ведомости за месяц ещё нет. */
  payroll: PersonHoursPayroll | null;
}

/**
 * Внести итог часов человека за месяц на одном объекте.
 *
 * Ключ операции обязателен на этой стороне, хотя сервер принимает и без него: обрыв связи и
 * двойное нажатие должны вернуть прежний результат, а не вторую запись. Замена итога повтор и без
 * ключа не удваивает, но ключ закрывает ещё и «тот же ключ с другими часами».
 */
export function setPersonMonthHours(
  siteId: string,
  employeeId: string,
  period: Период,
  hours: string,
  idempotencyKey: string,
): Promise<PersonMonthHoursOut> {
  return request(
    `/api/v1/timesheets/${encodeURIComponent(siteId)}/employees/${encodeURIComponent(employeeId)}/month-hours${периодом(period)}`,
    { method: 'PUT', body: { hours }, idempotencyKey },
  );
}
